import { slugify } from "@covertree/utils";
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Switch,
  Typography,
} from "@mui/material";
import { snakeCase } from "lodash";
import { alpha } from "@mui/material/styles";

import { CardMedia } from "../../cardMedia";
import { SwitchWithLabel } from "../../switch";
import { Tooltip } from "../../content";

import { ToggleCardProps } from "./types";

export const DesktopToggleCard = ({
  actions,
  actionText,
  actionSubText,
  checked,
  checkedCustomStyles,
  content,
  customizable,
  disabled,
  image,
  onToggle,
  subheader,
  switchLabels,
  subheaderVariant = "grey",
  title,
  tooltip,
  fontSize,
}: ToggleCardProps): JSX.Element => {
  const id = snakeCase(title);

  return (
    <Card
      role="option"
      aria-selected={checked}
      data-testid={`${slugify(title)}-card`}
      aria-labelledby={id}
      sx={(theme) => ({
        display: "flex",
        flexDirection: "row",
        border: `1px solid ${
          checked ? theme.palette.grey[300] : alpha(theme.colors.black, 0.12)
        }`,
        "&:hover": {
          boxShadow: theme.shadows[checked ? 0 : 2],
        },
        padding: theme.spacing(2.875),
        transition: theme.transitions.create(["background-color", "transform"]),
        backgroundColor: theme.palette.common.white,
        ...(checked
          ? {
              boxShadow: theme.shadows[0],
              ...checkedCustomStyles,
            }
          : {}),
      })}>
      {image && (
        <CardMedia data-testid={`${slugify(title)}-image`} image={image} />
      )}
      <Box flexGrow={1}>
        <CardHeader
          sx={{ alignItems: "flex-start" }}
          title={
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center">
              <Box
                role="heading"
                id={id}
                data-testid={`${slugify(title)}-title`}
                marginRight={1}>
                <Typography component="h5" fontSize={fontSize}>
                  {title}
                  {tooltip && (
                    <Tooltip
                      tooltip={tooltip}
                      dataTestId={`${slugify(title)}`}
                      placement="bottom"
                      fontSize={fontSize}
                      size="large"
                      sx={{
                        verticalAlign: "middle",
                        marginBottom: 0.25,
                        marginLeft: 1,
                      }}
                    />
                  )}
                </Typography>
              </Box>
              <Box
                marginLeft="auto"
                alignItems="center"
                display="flex"
                color={checked ? "secondary.main" : "text.secondary"}>
                <Box>
                  {actionText && (
                    <Typography
                      variant="h5"
                      fontSize={fontSize}
                      color={
                        actionSubText && !checked ? "text.primary" : "inherit"
                      }
                      sx={{ textWrap: "nowrap" }}>
                      {actionText}
                    </Typography>
                  )}
                  {actionSubText && (
                    <Typography
                      variant="subtitle1"
                      fontSize={fontSize}
                      color="inherit"
                      ml={2}
                      sx={{ textWrap: "nowrap" }}>
                      {actionSubText}
                    </Typography>
                  )}
                </Box>

                {customizable && switchLabels && (
                  <SwitchWithLabel
                    onChange={onToggle}
                    dataTestId={`${slugify(title)}-switch`}
                    value={checked}
                    labels={
                      Array.isArray(switchLabels) ? switchLabels : undefined
                    }
                  />
                )}
                {customizable && !switchLabels && (
                  <Switch
                    role="switch"
                    data-testid={`${slugify(title)}-switch`}
                    onClick={onToggle}
                    checked={checked}
                    sx={(theme) => ({
                      marginLeft: theme.spacing(2),
                    })}
                    disabled={disabled}
                  />
                )}
              </Box>
            </Box>
          }
          subheader={subheader}
          subheaderTypographyProps={{
            sx: (theme) => ({
              color:
                subheaderVariant === "black"
                  ? `${theme.palette.text.primary} !important`
                  : `${theme.palette.text.secondary} !important`,
            }),
          }}
        />
        {content && (
          <CardContent
            sx={(theme) => ({ padding: 0, marginTop: theme.spacing(1.25) })}>
            {" "}
            {content}{" "}
          </CardContent>
        )}
        {actions && (
          <CardActions
            sx={(theme) => ({
              width: "100%",
              padding: 0,
              marginTop: theme.spacing(3),
            })}>
            {actions}
          </CardActions>
        )}
      </Box>
    </Card>
  );
};
export default DesktopToggleCard;
